import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Target, BookOpen, Users, ArrowRight, Loader2, BarChart3, Mountain } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const About = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/about`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching about content:', error);
      setContent({});
    } finally {
      setLoading(false);
    }
  };

  // Default values
  const c = {
    hero_title: content?.hero_title || 'Our Mission',
    hero_subtitle: content?.hero_subtitle || 'Helping Dads Build Stronger Bonds',
    mission_title: content?.mission_title || 'Why We Do What We Do',
    mission_content: content?.mission_content || '',
    stats_title: content?.stats_title || 'The Power of an Involved Dad',
    stats_content: content?.stats_content || '',
    challenges_title: content?.challenges_title || "We Know It's Not Easy",
    challenges_content: content?.challenges_content || '',
    howto_title: content?.howto_title || 'Getting the Most Out of Dad Connect',
    howto_intro: content?.howto_intro || '',
    howto_1_title: content?.howto_1_title || 'Explore Activities',
    howto_1_content: content?.howto_1_content || '',
    howto_2_title: content?.howto_2_title || 'Dive Into Resources',
    howto_2_content: content?.howto_2_content || '',
    howto_3_title: content?.howto_3_title || 'Focus on Growth',
    howto_3_content: content?.howto_3_content || '',
    howto_4_title: content?.howto_4_title || 'Get the Book',
    howto_4_content: content?.howto_4_content || '',
    coaching_title: content?.coaching_title || 'Why Coaching is the Best Way to Grow',
    coaching_content: content?.coaching_content || '',
    bottomline_title: content?.bottomline_title || 'The Bottom Line',
    bottomline_content: content?.bottomline_content || '',
    cta_text: content?.cta_text || 'Start Your Journey Today',
    cta_button_text: content?.cta_button_text || 'Explore Activities',
    cta_button_link: content?.cta_button_link || '/activities',
    cta_coaching_text: content?.cta_coaching_text || 'Or learn about coaching',
    cta_coaching_link: content?.cta_coaching_link || '/coaching',
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="about-loading">
        <Loader2 className="h-12 w-12 animate-spin text-teal-500" />
      </div>
    );
  }

  const howtoItems = [
    { icon: Target, title: c.howto_1_title, content: c.howto_1_content, color: 'from-blue-400 to-blue-600' },
    { icon: BookOpen, title: c.howto_2_title, content: c.howto_2_content, color: 'from-amber-400 to-orange-500' },
    { icon: Heart, title: c.howto_3_title, content: c.howto_3_content, color: 'from-pink-400 to-red-500' },
    { icon: Users, title: c.howto_4_title, content: c.howto_4_content, color: 'from-green-400 to-teal-500' },
  ];

  // Helper to render content with line breaks
  const renderContent = (text) => {
    if (!text) return null;
    return text.split('\n\n').map((paragraph, i) => (
      <p key={i} className="mb-4 last:mb-0">{paragraph}</p>
    ));
  };

  // Helper to render content with markdown-style bold
  const renderMarkdown = (text) => {
    if (!text) return null;
    return text.split('\n\n').map((paragraph, i) => {
      // Handle bullet points
      if (paragraph.startsWith('- ') || paragraph.startsWith('• ')) {
        const items = paragraph.split('\n').filter(line => line.trim());
        return (
          <ul key={i} className="mb-4 last:mb-0 space-y-2">
            {items.map((item, j) => {
              const cleanItem = item.replace(/^[-•]\s*/, '');
              const parts = cleanItem.split(/(\*\*[^*]+\*\*)/g);
              return (
                <li key={j} className="flex items-start">
                  <span className="mr-2 mt-1.5 w-2 h-2 rounded-full bg-current flex-shrink-0" />
                  <span>
                    {parts.map((part, k) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={k}>{part.slice(2, -2)}</strong>;
                      }
                      return part;
                    })}
                  </span>
                </li>
              );
            })}
          </ul>
        );
      }
      
      // Convert **text** to bold
      const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="mb-4 last:mb-0">
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j}>{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="about-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent">
              {c.hero_title}
            </span>
          </h1>
          <p className="text-2xl text-gray-700 font-medium">
            {c.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="about-mission">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black mb-8 text-center">
            {c.mission_title}
          </h2>
          <div className="text-lg text-gray-700 leading-relaxed">
            {renderContent(c.mission_content)}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-teal-600 text-white" data-testid="about-stats">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-8">
            <BarChart3 className="h-10 w-10" />
            <h2 className="text-3xl md:text-4xl font-black text-center">
              {c.stats_title}
            </h2>
          </div>
          <div className="text-lg leading-relaxed opacity-95">
            {renderMarkdown(c.stats_content)}
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="about-challenges">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-8">
            <Mountain className="h-10 w-10 text-gray-700" />
            <h2 className="text-3xl md:text-4xl font-black text-center text-gray-900">
              {c.challenges_title}
            </h2>
          </div>
          <div className="text-lg text-gray-700 leading-relaxed">
            {renderMarkdown(c.challenges_content)}
          </div>
        </div>
      </section>

      {/* How To Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="about-howto">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black mb-4 text-center">
            {c.howto_title}
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            {c.howto_intro}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {howtoItems.map((item, index) => (
              <Card key={index} className="border-4 border-gray-200 hover:border-teal-400 transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      <item.icon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.content}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Coaching Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-500 to-blue-600 text-white" data-testid="about-coaching">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black mb-8 text-center">
            {c.coaching_title}
          </h2>
          <div className="text-lg leading-relaxed opacity-95">
            {renderMarkdown(c.coaching_content)}
          </div>
          <div className="mt-8 text-center">
            <Link to="/coaching">
              <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 font-bold px-8 py-6 text-lg" data-testid="coaching-cta-btn">
                Learn About Coaching
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom Line Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="about-bottomline">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black mb-8 text-center">
            {c.bottomline_title}
          </h2>
          <div className="text-lg text-gray-700 leading-relaxed text-center">
            {renderContent(c.bottomline_content)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-400 to-teal-500" data-testid="about-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-8 text-white">
            {c.cta_text}
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={c.cta_button_link}>
              <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 font-bold px-8 py-6 text-lg" data-testid="primary-cta-btn">
                {c.cta_button_text}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to={c.cta_coaching_link}>
              <Button size="lg" variant="outline" className="border-4 border-white text-white hover:bg-white hover:text-teal-600 font-bold px-8 py-6 text-lg" data-testid="secondary-cta-btn">
                {c.cta_coaching_text}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
